import React, { useState } from 'react';
import { CmsButton, CmsFeature } from '../../types/contentstack';
import LinkIcon from '../icons/LinkIcon';
import { getAssetUrl } from '../../utils/assetUrl';
import Modal from '../Modal/Modal';
import { ModalSize } from '../Modal/Modal.types';
import DynamicPage from '../../pages/DynamicPage';

interface CmsFeaturesData {
  name?: string;
  component?: string;
  features: CmsFeature[];
}

interface CmsFeaturesProps {
  data: CmsFeaturesData;
  blockMargin?: boolean;
  className?: string;
}

export const CmsFeatures: React.FC<CmsFeaturesProps> = ({
  data,
  blockMargin = true,
  className = '',
}) => {
  const isColumnImageText = data.component === 'CmsColumnImageText';
  const isBenefitForAll = data.name === 'BenefitForAll';
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    modalId: string;
    title: string;
  }>({ isOpen: false, modalId: '', title: '' });
  const [footerButtons, setFooterButtons] = useState<CmsButton[]>([]);

  const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, text: string) => {
    if (url?.startsWith('modal://')) {
      e.preventDefault();
      const modalId = url.replace('modal://', '');
      setModalState({ isOpen: true, modalId, title: text });
    }
  };

  const closeModal = () => {
    setModalState({ isOpen: false, modalId: '', title: '' });
    setFooterButtons([]);
  };

  const handleFooterExtracted = (buttons: CmsButton[]) => {
    setFooterButtons(buttons);
  };

  const getImageFitClass = (imageFit?: string) => {
    if (imageFit === 'none') return 'object-none';
    return 'object-cover';
  };

  const getPaddingClass = () => {
    if (!blockMargin) return 'px-500';
    if (isColumnImageText) return 'pt-[103px] pb-0';
    return 'pt-800 pb-800 px-500';
  };

  const getProseClass = () => {
    const base =
      'text-[#192026] prose prose-lg max-w-none' +
      ' [&_h4]:!font-bold [&_h4]:!leading-[1.13] [&_h4]:!tracking-[-0.03em] [&_h4]:!text-[#34363B]:!max-w-[564px]' +
      ' [&_h3]:!font-bold [&_h3]:!leading-[1.13] [&_h3]:!tracking-[-0.03em] [&_h3]:!text-[#192026] [&_h3]:!max-w-[564px]' +
      ' [&_h5]:!font-bold [&_h5]:!leading-[1.13] [&_h5]:!tracking-[-0.03em] [&_h5]:!text-[#192026] [&_h5]:!max-w-[450px] [&_h5]:!text-[48px]' +
      ' [&_p]:!font-medium [&_p]:!leading-[1.63] [&_p]:!tracking-normal [&_p]:!mb-[20px] [&_p]:!text-[#192026] [&_p]:!max-w-[560px] ';

    if (isColumnImageText) {
      return `${base} [&_h4]:!text-[clamp(24px,2.5vw,48px)] [&_h3]:!text-[clamp(28px,3.5vw,48px)] [&_p]:!text-[20px]`;
    }
    if (isBenefitForAll) {
      return `${base} [&_h4]:!text-[32px] [&_h3]:!text-[64px] [&_h3]:!max-w-[460px] [&_p]:!text-[20px]`;
    }
    return `${base} [&_h4]:!text-[32px] [&_h3]:!text-[64px] [&_p]:!text-[20px]`;
  };

  return (
    <div
      className={`relative w-full ${getPaddingClass()} ${isBenefitForAll ? 'overflow-hidden' : ''} ${className}`}
    >
      {isBenefitForAll && (
        <>
          <img
            src={getAssetUrl('unauth/Cat-Paw.png')}
            alt=""
            className="absolute bottom-[80px] left-0 h-12 z-20 opacity-80"
          />
          <img
            src={getAssetUrl('unauth/Cat-Paw.png')}
            alt=""
            className="absolute -bottom-2 -left-2 h-12 z-20 opacity-80"
          />
        </>
      )}
      <div className={`relative z-10 mx-auto ${isColumnImageText ? '' : 'max-w-7xl'}`}>
        {data.name && !isBenefitForAll && (
          <h2 className="text-center mb-16 font-bold text-heading-200 leading-tight text-black/90">
            {data.name}
          </h2>
        )}
        <div className={isColumnImageText ? '' : 'space-y-24'}>
          {data.features.map((feature) => {
            const isImageLandscape = feature.image_fit === 'contain';
            const firstButtonIconPosition = feature?.image_alignment;
            const imageOnLeft = firstButtonIconPosition === 'Right' ? false : true;
            return (
              <article
                key={feature.heading}
                className={`flex flex-col ${
                  imageOnLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } ${isColumnImageText ? 'gap-4 md:gap-6 lg:gap-12' : 'gap-8 lg:gap-12'} items-start`}
              >
                {feature.image && (
                  <div
                    className={`w-full ${
                      isImageLandscape
                        ? 'lg:w-[737px] flex-shrink-0'
                        : isColumnImageText
                          ? 'lg:w-[40%] lg:self-end'
                          : 'flex-1'
                    }`}
                  >
                    <img
                      src={feature.image.url}
                      alt={feature.image.description}
                      className={`block w-full ${
                        isImageLandscape
                          ? 'rounded-400 h-[491px] object-cover'
                          : `${isColumnImageText ? 'rounded-t-400 rounded-b-none h-[450px]' : 'rounded-400'} ${feature.unlock_image_height || isColumnImageText ? '' : 'aspect-[519/649]'} ${getImageFitClass(feature.image_fit)}`
                      }`}
                    />
                  </div>
                )}
                <div
                  className={`w-full ${isColumnImageText ? 'lg:flex-1  px-500 ml-[34px]' : `lg:basis-[628px] ${isBenefitForAll || isImageLandscape ? '' : ' lg:pt-[67px]'}`} flex flex-col gap-6 ${!isColumnImageText && feature.image_alignment === 'Left' ? 'lg:pl-[5rem]' : ''}`}
                >
                  {feature.heading && (
                    <div>
                      <h3
                        className={`${isColumnImageText ? 'text-heading-300 max-w-md' : 'text-[64px]'} font-bold leading-[1.13] tracking-[-0.03em] text-[#192026]`}
                      >
                        {feature.heading}
                      </h3>
                    </div>
                  )}
                  <div
                    className={getProseClass()}
                    dangerouslySetInnerHTML={{ __html: feature.body }}
                  />
                  {feature.buttons && feature.buttons.filter((b) => b.text || b.url).length > 0 && (
                    <div className="flex gap-3 flex-wrap mt-2">
                      {feature.buttons
                        .filter((b) => b.text || b.url)
                        .map((button, i) => {
                          const isModalButton = button.url?.startsWith('modal://');
                          return (
                            <a
                              key={`${feature.heading}-button-${i}`}
                              href={button.url}
                              onClick={(e) => handleButtonClick(e, button.url, button.text)}
                              target={button.external ? '_blank' : '_self'}
                              rel={button.external ? 'noopener noreferrer' : undefined}
                              className={`flex items-center rounded-md font-semibold transition-colors text-[#0054A4] lg:text-lg ${
                                button.variant === 'filled' &&
                                'bg-blue-600 text-white hover:bg-blue-700 px-8 py-3'
                              } ${isModalButton ? 'cursor-pointer' : ''}`}
                            >
                              {button.text}
                              <LinkIcon
                                type={button.icon}
                                className={
                                  button.icon === 'ExternalIcon' ? 'w-4 h-4 ml-1' : 'w-5 h-5 ml-2'
                                }
                              />
                            </a>
                          );
                        })}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={''}
        size={ModalSize.Large}
        footerContent={
          footerButtons.length > 0 ? (
            <div className="flex gap-3 flex-wrap justify-end">
              {footerButtons.map((button, i) => (
                <a
                  key={`footer-button-${i}`}
                  href={button.url}
                  target={button.external ? '_blank' : '_self'}
                  rel={button.external ? 'noopener noreferrer' : undefined}
                  className={`flex items-center rounded-md font-semibold transition-colors text-[#0054A4] lg:text-lg ${
                    button.variant === 'filled' &&
                    'bg-[rgba(0,84,164,1)] text-white hover:bg-blue-700 px-8 py-3'
                  }`}
                >
                  {button.text}
                  <LinkIcon
                    type={button.icon}
                    className={button.icon === 'ExternalIcon' ? 'w-4 h-4 ml-1' : 'w-5 h-5 ml-2'}
                  />
                </a>
              ))}
            </div>
          ) : undefined
        }
      >
        <DynamicPage
          slug={modalState.modalId}
          header={false}
          onFooterExtracted={handleFooterExtracted}
        />
      </Modal>
    </div>
  );
};

export default CmsFeatures;
